import React, { useCallback, useEffect, useRef, useState } from 'react';
import matterportService from '../services/matterportService';
import { useLanguage } from '../context/LanguageContext';
import { Z_INDEX } from '../const';
import quickNavConfig from '../config/quickNavConfig.json';
import './LocationImageOverlay.css';

/**
 * Fullscreen image overlay that replaces the Matterport 3D viewer for stops
 * configured with an `imageOverlay` block in `quickNavConfig.json`.
 *
 * Triggers:
 *   • Footer quick-nav arrival → `activeLocationChanged` event.
 *   • Guided-tour step          → `tourStep` event from matterportService.
 *
 * Per-stop auto-open control (in `quickNavConfig.locations[].imageOverlay`):
 *   "autoOpen": { "quicknav": true, "tour": true }
 *   - quicknav: open after arriving via the footer quick-nav (default true)
 *   - tour:     open while the guided tour reaches this stop (default true)
 *   When false for a trigger, the overlay is suppressed for that source;
 *   the user can still see images via the proximity tile strip / lightbox.
 *   Backward compatible: if `autoOpen` is missing, both default to true.
 *
 * After arriving, the overlay opens after `imageOverlay.delayMs` (default
 * 5000 ms) so the visitor briefly sees the 3D camera land. Images
 * auto-advance every `imageOverlay.autoAdvanceMs` (default 5000 ms) and
 * loop. The overlay sits below the temple header + footer dock so all
 * navigation chrome remains accessible — clicking another stop, advancing
 * the tour, or pressing × closes the overlay and returns to 3D.
 */
const LocationImageOverlay = () => {
  const { language } = useLanguage();
  // Resolved location config currently being shown (null when hidden)
  const [overlayLoc, setOverlayLoc] = useState(null);
  const [imageIdx, setImageIdx] = useState(0);
  // Where the overlay was triggered from. 'quicknav' stops after one full
  // cycle and reveals the 3D viewer; 'tour' keeps looping until the next
  // tourStep / tourStop event.
  const [source, setSource] = useState(null);
  // While `closing` is true the side-panel plays its slide-out keyframe
  // before unmounting. The CSS animation duration must match CLOSE_ANIM_MS.
  const [closing, setClosing] = useState(false);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const CLOSE_ANIM_MS = 320;

  const cancelOpenTimer = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const cancelCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  // Tear down immediately, no animation. Used when another stop replaces
  // the overlay (a fresh slide-in plays for the new location).
  const teardown = useCallback(() => {
    cancelCloseTimer();
    setClosing(false);
    setOverlayLoc(null);
    setSource(null);
  }, [cancelCloseTimer]);

  // Close any current overlay and schedule a new one if `loc` has images.
  // Called from both `activeLocationChanged` (manual nav) and `tourStep`
  // (guided tour). Locations without `imageOverlay` simply hide the overlay.
  // Per-stop `imageOverlay.autoOpen.{quicknav,tour}` (default true) gates
  // whether to actually open for the given trigger source.
  const scheduleOverlay = useCallback((loc, triggerSource) => {
    cancelOpenTimer();
    teardown();
    setImageIdx(0);
    // Never schedule the image overlay over the aerial drone view — the
    // Sky View stop replaces the 3D viewer with a 360 pano, and queued
    // images from the previous stop would otherwise appear on top of it.
    if (loc?.action === 'skyView') return;
    const cfg = loc?.imageOverlay;
    if (!cfg?.images?.length) return;
    // Honour per-stop auto-open flags. Missing entry = enabled (back-compat).
    const autoOpen = cfg.autoOpen;
    if (autoOpen && autoOpen[triggerSource] === false) return;
    // Skip the side-panel (¼-window) variant on mobile — there isn't
    // enough horizontal room next to the 3D viewer for a useful panel,
    // and the dedicated gallery / fullscreen flows already cover this
    // case on small screens.
    if (
      cfg.displayMode === 'sidePanel' &&
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(max-width: 820px)').matches
    ) {
      return;
    }
    const delay = typeof cfg.delayMs === 'number' ? cfg.delayMs : 5000;
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null;
      setOverlayLoc(loc);
      setImageIdx(0);
      setSource(triggerSource);
    }, delay);
  }, [cancelOpenTimer, teardown]);

  // Animated close — plays the slide-out keyframe before unmounting for
  // the side-panel mode. Fullscreen mode has no exit animation, so it
  // tears down immediately.
  const close = useCallback(() => {
    cancelOpenTimer();
    cancelCloseTimer();
    const isSide = overlayLoc?.imageOverlay?.displayMode === 'sidePanel';
    if (!isSide) {
      setOverlayLoc(null);
      setSource(null);
      return;
    }
    setClosing(true);
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setClosing(false);
      setOverlayLoc(null);
      setSource(null);
    }, CLOSE_ANIM_MS);
  }, [cancelOpenTimer, cancelCloseTimer, overlayLoc]);

  // Footer quick-nav: LocationQuickNav dispatches activeLocationChanged
  // with { tourSid, id } once the camera has arrived at the stop.
  useEffect(() => {
    const onActive = (e) => {
      const id = e.detail?.id;
      const loc = quickNavConfig.locations.find((l) => l.id === id);
      scheduleOverlay(loc, 'quicknav');
    };
    window.addEventListener('activeLocationChanged', onActive);
    return () => window.removeEventListener('activeLocationChanged', onActive);
  }, [scheduleOverlay]);

  // Guided tour: matterportService emits `tourStep` for every step. Map the
  // step to a quickNav location and re-schedule. `tourStop` clears the
  // overlay immediately so the user sees the 3D again after the tour is
  // stopped.
  //
  // Multiple stops may share the same `tourSid` (e.g. Mahaveer Bandhara
  // reuses the Mandir Entrance sweep but has its own narration + images).
  // Matching purely by SID would always return the first such location and
  // show the wrong overlay. We disambiguate by the tour step `index`, which
  // matches the position of the stop inside the same filter used to build
  // the tour order in LocationQuickNav (`!excludeFromTour`).
  useEffect(() => {
    const onTourStep = ({ sid, index }) => {
      const tourLocations = quickNavConfig.locations.filter(
        (l) => !l.excludeFromTour
      );
      const loc =
        (typeof index === 'number' && tourLocations[index]) ||
        quickNavConfig.locations.find((l) => l.tourSid === sid);
      scheduleOverlay(loc, 'tour');
    };
    const onTourStop = () => {
      cancelOpenTimer();
      teardown();
    };
    matterportService.addEventListener('tourStep', onTourStep);
    matterportService.addEventListener('tourStop', onTourStop);
    return () => {
      matterportService.removeEventListener('tourStep', onTourStep);
      matterportService.removeEventListener('tourStop', onTourStop);
    };
  }, [scheduleOverlay, cancelOpenTimer, teardown]);

  // Reset auto-advance whenever the location changes
  useEffect(() => () => cancelOpenTimer(), [cancelOpenTimer]);

  // Aerial drone view takeover — Sky View dispatches `open360View` to
  // replace the 3D viewer with a 360 pano. Any pending or visible image
  // overlay (e.g. nursery, the stop right before Sky View) must be torn
  // down so its slideshow doesn't render on top of the drone view.
  useEffect(() => {
    const onSky = () => {
      cancelOpenTimer();
      teardown();
    };
    window.addEventListener('open360View', onSky);
    return () => window.removeEventListener('open360View', onSky);
  }, [cancelOpenTimer, teardown]);

  const images = overlayLoc?.imageOverlay?.images || [];

  // Auto-advance: each image stays for autoAdvanceMs.
  // - 'quicknav' fullscreen + sidePanel: stop on the last image and
  //   close — fullscreen returns to the 3D view; sidePanel collapses
  //   back to the gallery tile strip after one full cycle.
  // - 'tour' sidePanel: play one cycle then HOLD on the last image
  //   until the next tourStep / tourStop event replaces or clears the
  //   overlay. No looping, no auto-close — the photo stays as context
  //   beside the 3D scene for the rest of the stop.
  // - 'tour' fullscreen: keep looping (existing behaviour).
  useEffect(() => {
    if (!overlayLoc || images.length === 0) return undefined;
    const advance = overlayLoc.imageOverlay.autoAdvanceMs ?? 5000;
    const isSide = overlayLoc.imageOverlay?.displayMode === 'sidePanel';
    const isLast = imageIdx >= images.length - 1;
    // Side-panel tour: hold the last image; don't schedule another tick.
    if (isLast && isSide && source === 'tour') return undefined;
    const t = setTimeout(() => {
      if (isLast && source === 'quicknav') {
        close();
      } else if (isLast && isSide) {
        // Side-panel quicknav fell through to here only when the cycle
        // completed; close to collapse.
        close();
      } else if (images.length > 1) {
        setImageIdx((i) => (i + 1) % images.length);
      }
    }, advance);
    return () => clearTimeout(t);
  }, [overlayLoc, imageIdx, images.length, source, close]);

  // Keyboard nav while open: Esc closes; arrows move
  useEffect(() => {
    if (!overlayLoc) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft')
        setImageIdx((i) => (i - 1 + images.length) % images.length);
      else if (e.key === 'ArrowRight')
        setImageIdx((i) => (i + 1) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlayLoc, images.length, close]);

  if (!overlayLoc) return null;

  const cur = images[imageIdx];
  const title =
    overlayLoc.labels?.[language] || overlayLoc.labels?.en || '';
  const caption =
    cur?.caption?.[language] || cur?.caption?.en || '';
  const src = cur?.src
    ? (/^(data:|https?:|blob:)/i.test(cur.src)
        ? cur.src
        : process.env.PUBLIC_URL + cur.src)
    : '';

  // Display mode: "fullscreen" (default — covers the viewport) or
  // "sidePanel" (right-edge ~quarter window so the 3D viewer stays
  // visible + interactive alongside). Falls back to fullscreen for
  // unknown values, including legacy configs without the field.
  const isSidePanel = overlayLoc.imageOverlay?.displayMode === 'sidePanel';
  // Orientation override: per-image `orientation` wins over the stop-level
  // `imageOverlay.orientation` default. Accepts 'landscape' | 'portrait'.
  // Only meaningful in side-panel mode; ignored for fullscreen.
  const orientation =
    cur?.orientation || overlayLoc.imageOverlay?.orientation || 'portrait';
  const isLandscape = isSidePanel && orientation === 'landscape';
  const rootClass = [
    'loc-img-overlay',
    isSidePanel && 'loc-img-overlay--side',
    isLandscape && 'loc-img-overlay--side-landscape',
    isSidePanel && closing && 'loc-img-overlay--closing',
  ]
    .filter(Boolean)
    .join(' ');

  const prev = (e) => {
    e.stopPropagation();
    setImageIdx((i) => (i - 1 + images.length) % images.length);
  };
  const next = (e) => {
    e.stopPropagation();
    setImageIdx((i) => (i + 1) % images.length);
  };

  return (
    <div
      className={rootClass}
      style={{ zIndex: Z_INDEX.LOCATION_IMAGE_OVERLAY }}
      role="dialog"
      aria-modal="false"
      aria-label={title || caption}
    >
      <img
        key={imageIdx}
        className="loc-img-overlay__img"
        src={src}
        alt={caption}
      />

      {title && (
        <div className="loc-img-overlay__title">{title}</div>
      )}

      {images.length > 1 && (
        <div className="loc-img-overlay__counter">
          {imageIdx + 1} / {images.length}
        </div>
      )}

      <button
        type="button"
        className="loc-img-overlay__close"
        onClick={close}
        aria-label={isSidePanel ? 'Collapse to gallery' : 'Close gallery'}
        title={isSidePanel ? 'Collapse' : 'Close'}
      >
        {isSidePanel ? (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* Right-pointing chevron — "collapse back into the tile strip" */}
            <path d="M9 6l6 6-6 6" />
          </svg>
        ) : (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        )}
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            className="loc-img-overlay__nav loc-img-overlay__nav--prev"
            onClick={prev}
            aria-label="Previous image"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <button
            type="button"
            className="loc-img-overlay__nav loc-img-overlay__nav--next"
            onClick={next}
            aria-label="Next image"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>

          <div className="loc-img-overlay__dots">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`loc-img-overlay__dot${
                  i === imageIdx ? ' loc-img-overlay__dot--active' : ''
                }`}
                onClick={() => setImageIdx(i)}
                aria-label={`Image ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LocationImageOverlay;
